import { Exchange, SegmentType } from 'modules/Search/constants';
import {
  COMMODITY_SEGMENT,
  CURRENCY_SEGMENT,
  EQUITIES_SEGMENT,
  OPTIONS_SEGMENT,
} from './consts';

// validation service
class ValidationService {
  isObject = (obj: BaseObject) =>
    obj !== null && typeof obj === 'object' && Array.isArray(obj) === false;

  isLength = (value: string, min?: number, max?: number): boolean => {
    if (min && value.length < min) {
      return false;
    }

    if (max && value.length > max) {
      return false;
    }

    return true;
  };

  isNumberInRange = (value: number, min?: number, max?: number): boolean => {
    if (min !== undefined && value <= min) {
      return false;
    }

    if (max !== undefined && value >= max) {
      return false;
    }

    return true;
  };

  isNotEmpty = (value: string | [] | Record<string, unknown>): boolean => {
    // if array
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // if object
    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    // if string
    return !!value;
  };

  isDigitOnly = (value: string): boolean => {
    const digitRegExp = /^[0-9]+$/;

    return digitRegExp.test(value);
  };

  isUserId = (value: string | null): boolean => {
    if (!!value && this.isLength(value, 6, 6)) {
      return true;
    }

    return false;
  };

  validateVPA = (vpa: string) =>
    /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
      vpa,
    );

  isIndiaPhoneNumber = (value: string): boolean =>
    // should be 10 digits
    // should starts with 6/7/8/9
    /^[6,7,8,9]\d{9}$/.test(value);

  isPANCard = (value: string) =>
    // should be 10 characters
    // first 5 alphabets
    // next 4 digits
    // last 1 alphabet
    /^[a-zA-Z]{5}\d{4}[a-zA-Z]{1}$/.test(value);

  isEmail = (value: string) =>
    /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value);

  isPasswordStrengthValid = (value: string) =>
    this.passwordStrength(value).valid;

  hasSpecialChar = (value: string): boolean => /[!@#$%*_+\-=\\~]/g.test(value);

  hasNumber = (value: string): boolean => /\d/.test(value);

  hasAlpha = (value: string): boolean => /[A-Za-z]/.test(value);

  passwordStrength(password: string): PasswordStrength {
    const result = {
      valid: false,
      percentage: 0,
      steps: {
        hasSpecialChar: false,
        hasNumber: false,
        hasAlpha: false,
        validLength: false,
      },
    };

    const percentagePerStep = 100 / Object.keys(result.steps).length;

    const setValidationError = (step: keyof PasswordStrength['steps']) => {
      const { steps } = result;

      steps[step] = true;
      result.percentage += percentagePerStep;

      if (
        steps.hasNumber &&
        steps.hasSpecialChar &&
        steps.validLength &&
        steps.hasAlpha
      ) {
        result.valid = true;
        result.percentage = 100;
      }
    };

    if (this.isLength(password, 6, 12)) {
      setValidationError('validLength');
    }

    if (this.hasSpecialChar(password)) {
      setValidationError('hasSpecialChar');
    }

    if (this.hasNumber(password)) {
      setValidationError('hasNumber');
    }

    if (this.hasAlpha(password)) {
      setValidationError('hasAlpha');
    }

    return result;
  }

  isFloatPositiveNumber = (amount: string) => {
    const hasDot = amount.includes('.');

    if (!/^(\d*)(\.)?\d*$/.test(amount) || amount === '.') {
      // when user enter not digit or more than one dot or only one dot
      return 'changeCursorPosition';
    }
    if (hasDot) {
      // only two digit after dot or less
      const splittedValue = amount.split('.');
      const valueBeforeDot = splittedValue[0];
      const valueAfterDot = splittedValue[1];
      return `${valueBeforeDot}.${valueAfterDot.substr(0, 2)}`;
    }
    // user don't have dot
    return amount;
  };

  isNumberWithDecimalDigit = (value: string, decimalDigit: number): boolean =>
    RegExp(`^-?[0-9]{0,}[.]{0,1}[0-9]{0,${decimalDigit}}$`).test(value);

  userIdPipeValidation = (length: number) => (
    val: string,
    config: PipeConfig,
  ) => {
    const { rawValue, previousConformedValue, placeholderChar } = config;

    if (previousConformedValue !== rawValue) {
      const pattern = new RegExp(/[^a-z0-9]/gi);

      const raw: string = rawValue
        .split('')
        .reduce((acc: string, value: string) => {
          if (!pattern.test(value)) {
            return `${acc}${value}`.trim();
          }

          return acc;
        }, '');

      if (raw.length > length) {
        return previousConformedValue;
      }

      if (raw.length < length) {
        return raw.padEnd(length, placeholderChar);
      }

      return raw;
    }

    return val;
  };

  isMultiple = (x: number, y: number) => Math.round(x / y) / (1 / y) === x;

  isEquities = (segment: SegmentType) =>
    (segment ? EQUITIES_SEGMENT.includes(segment) : false);

  isSimpleOrder = (product?: Product | Products) =>
    product === 'D' ||
    product === 'I' ||
    product === 'Intraday' ||
    product === 'Delivery';

  isCommodity = (segment?: SegmentType) =>
    (segment ? COMMODITY_SEGMENT.includes(segment) : false);

  isFnO = (segment: SegmentType) =>
    (segment ? OPTIONS_SEGMENT.includes(segment) : false);

  isCurrency = (segment: SegmentType) =>
    (segment ? CURRENCY_SEGMENT.includes(segment) : false);

  isIndex(instrument: Series) {
    return instrument === 'INDEX';
  }

  isOption(instrument: Series) {
    return instrument === 'OPT';
  }

  isNumber(value: number | string | null) {
    return typeof value === 'number' && !Number.isNaN(value);
  }

  isFUTorOPT(instrument?: Series) {
    return instrument === 'FUT' || instrument === 'OPT';
  }

  isNFOOrder(scrip: DataSchema | null) {
    const instrument = scrip?.instrument;
    const exchange = scrip?.exchange;

    return this.isFUTorOPT(instrument) && exchange === Exchange.NSE;
  }

  isBFOOrder(scrip: DataSchema | null) {
    const instrument = scrip?.instrument;
    const exchange = scrip?.exchange;

    return this.isFUTorOPT(instrument) && exchange === Exchange.BSE;
  }

  isEdisRequired = (
    segment: SegmentType,
    orderType: OrderEntryType,
    side: OrderEntrySide,
  ) =>
    orderType === 'Delivery' &&
    side === 'Sell' &&
    this.isEquities(segment);

  isPriceAlertValueValid = (value: string) => {
    const isIntegerLengthValid =
      Math.round(Number(value)).toString().length < 10;
    const areDigitsValid =
      !value.includes('.') ||
      (value.includes('.') && value.split('.')[1].length < 5);

    return (
      !Number.isNaN(Number(value)) && isIntegerLengthValid && areDigitsValid
    );
  };

  isInvitePhoneNumberValueValid = (value: string, phoneNumberLength: number) =>
    value.length <= phoneNumberLength && !Number.isNaN(Number(value));

  isExchangeToken = (value: string) =>
    value.match(/\w{3}_\w{2,}\|[\d|\w|\s]{1,}/g);
}

export default new ValidationService();
